//@flow
import React from 'react'
import styled from 'styled-components'
import { ButtonSmall, ButtonMedium, ButtonLarge } from '../TextPrimitives'
import ReactSVG from 'react-svg'
import { shadowLow, shadowMedium } from '../../style-functions'
import RightIcon from '../../icons/ArrowRight.svg'
import LeftIcon from '../../icons/ArrowLeft.svg'
import BookIcon from '../../icons/Book.svg'
import FlagIcon from '../../icons/Flag.svg'
import CrossIcon from '../../icons/Cross.svg'
import GroupIcon from '../../icons/Group.svg'
import _ from 'lodash'

const svgs = {
  right: RightIcon,
  left: LeftIcon,
  book: BookIcon,
  flag: FlagIcon,
  cross: CrossIcon,
  group: GroupIcon,
}

const SMALL = 'small'
const MEDIUM = 'medium'
const LARGE = 'large'
const ROUND = 'round'
const RECT = 'rect'
const CHIP = 'chip'

const padding = (values, scale) => values.map(v => v * scale + 'rem').join(' ')

const scale = size => (size === LARGE ? 3 : size === MEDIUM ? 2 : 1)

const scaleIcon = ({ size }) => {
  const width = size === SMALL ? 1.325 : size === LARGE ? 5 : 2.5
  const w = width + 'rem'
  return { width: w, height: w }
}

const buttonSize = ({ size }) => {
  return `${3 * scale(size)}rem`
}

const roundSize = ({ size }) => {
  return `${2.5 * scale(size)}rem`
}

const adjustWidth = ({ width }) => {
  const w = width ? width : 'initial'
  return { width: w }
}

const baseStyle = btn => styled(btn)`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  transition-property: background, color;
  transition-duration: 100ms;
  transition-timing-function: ease-in-out;
  border: none;
  outline: 0;
  background: ${({ theme, inverse, type, bg }) => {
    if (bg) return bg
    const { dark, light, mediumDark } = theme.colors.greys
    return inverse ? (type === CHIP ? mediumDark : dark) : light
  }};
  color: ${({ theme, inverse: isInverse }) => {
    const { inverse, normal } = theme.colors.text
    return isInverse ? inverse : normal
  }};
  cursor: pointer;
  user-select: none;
  white-space: nowrap;

  :focus {
    outline: 0;
  }

  :enabled {
    ${({ theme, raised }) => (raised ? shadowLow({ theme }) : null)};
    :active {
      ${({ theme, raised }) => (raised ? shadowMedium({ theme }) : null)};
    }
  }
`

const chipStyle = btn => styled(btn)`
  ${adjustWidth};
  height: ${buttonSize};
  border-radius: ${buttonSize};
  padding: ${({ size }) => padding([0, 3.5], scale(size))};
`

const rectStyle = btn => styled(btn)`
  ${adjustWidth};
  height: ${buttonSize};
  padding: ${({ icon, size }) => {
    const s = scale(size)
    return icon ? padding([0, 2, 0, 1], s) : padding([0, 2], s)
  }};
  }
  border-radius: ${({ size }) => `${0.5 * scale(size)}rem`};
`

const roundStyle = btn => styled(btn)`
  width: ${roundSize};
  height: ${roundSize};
  border-radius: ${roundSize};
`

const ChipS = chipStyle(baseStyle(ButtonSmall))
const ChipM = chipStyle(baseStyle(ButtonMedium))
const ChipL = chipStyle(baseStyle(ButtonLarge))

const RectS = rectStyle(baseStyle(ButtonSmall))
const RectM = rectStyle(baseStyle(ButtonMedium))
const RectL = rectStyle(baseStyle(ButtonLarge))

const RoundS = roundStyle(baseStyle(ButtonSmall))
const RoundM = roundStyle(baseStyle(ButtonMedium))
const RoundL = roundStyle(baseStyle(ButtonLarge))

const Icon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  /* border: 1px solid red; */

  svg {
    width: auto;
    height: 100%;
    /* border: 1px solid green; */
  }

  path,
  polygon {
    fill: ${({ theme, inverse }) => {
      const { light, dark } = theme.colors.greys
      return inverse ? light : dark
    }};
  }
`

const ChipIcon = styled(Icon)`
  position: absolute;
  background: ${({ theme, inverse }) => {
    const { mediumLight, dark } = theme.colors.greys
    return inverse ? dark : mediumLight
  }};

  ${({ size }) => {
    const height = scale(size) * 3
    const gap = 0.3
    const width = height - 2 * gap + 'rem'
    return { width, height: width, left: gap + 'rem' }
  }};
  border-radius: 100%;

  svg {
    transform: scale(0.6);
  }
`

const RectIcon = styled(Icon)`
  position: relative;
  left: -0.33rem;
  ${scaleIcon};
`

const RoundIcon = styled(Icon)`
  ${scaleIcon};
`

const ReactSVGStyled = styled(ReactSVG)`
  width: 100%;
  height: 100%;

  > div {
    width: 100%;
    height: 100%;
  }
`

type Sizes = 'small' | 'medium' | 'large'

type Shapes = 'round' | 'rect' | 'chip'

type Icons = $Keys<typeof svgs>

type ButtonProps = {
  text: string,
  size: Sizes,
  inverse: boolean,
  raised: boolean,
  disabled: boolean,
  raised: boolean,
  width: number | string,
  type: Shapes,
  icon: Icons,
  bg: string,
  onClick: Function,
}

// TODO: (3) Separate into 3 components
const Button = (props: ButtonProps) => {
  const { text, type, icon, size, children } = props
  const iconProps = _.omit(props, 'className')
  const ic = icon ? <ReactSVGStyled src={svgs[icon]} /> : null
  const content = (
    <>
      {icon && <>{type === RECT && <RectIcon {...iconProps}>{ic}</RectIcon>}</>}
      {icon && (
        <>{type === ROUND && <RoundIcon {...iconProps}>{ic}</RoundIcon>}</>
      )}
      {icon && <>{type === CHIP && <ChipIcon {...iconProps}>{ic}</ChipIcon>}</>}
      {(type === RECT || type === CHIP || !icon) && (
        <span>{text || children}</span>
      )}
    </>
  )

  if (type === CHIP) {
    if (size === SMALL) return <ChipS {...props}>{content}</ChipS>
    if (size === LARGE) return <ChipL {...props}>{content}</ChipL>
    return <ChipM {...props}>{content}</ChipM>
  } else if (type === ROUND) {
    if (size === SMALL) return <RoundS {...props}>{content}</RoundS>
    if (size === LARGE) return <RoundL {...props}>{content}</RoundL>
    return <RoundM {...props}>{content}</RoundM>
  } else {
    if (size === SMALL) return <RectS {...props}>{content}</RectS>
    if (size === LARGE) return <RectL {...props}>{content}</RectL>
    return <RectM {...props}>{content}</RectM>
  }
}

Button.defaultProps = {
  size: MEDIUM,
  text: '',
  type: RECT,
  onClick: x => x,
}

export default Button
